import React from 'react';
import { Button } from 'antd';
import { css } from '@emotion/react';
import Tooltip from 'components/common/tooltip';
import styled from 'types/theme.types';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';

export const IconButton: React.FunctionComponent<{
  /** Omit where Figma specs no tooltip. */
  tooltipTitleId?: string;
  icon: React.FC<Omit<AntdIconProps, 'ref'> & React.RefAttributes<HTMLSpanElement>>;
  dataE2e: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  /** A button wrapping the icon needs this or it is unnamed. */
  ariaLabel?: string;
  /** For a button that discloses content (the tree chevron): the aria-expanded state. */
  ariaExpanded?: boolean;
  /** -1 for a button inside a link, so the anchor is the action's only tab stop. */
  tabIndex?: number;
  /** Forwarded so a call site can extend this with `styled(IconButton)` instead of a wrapper div. */
  className?: string;
}> = ({
  tooltipTitleId,
  icon: Icon,
  dataE2e,
  onClick,
  disabled,
  size = 'middle',
  ariaLabel,
  ariaExpanded,
  tabIndex,
  className
}) => {
  const button = (
    <StyledIconButton
      type="text"
      size={size}
      icon={<Icon />}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      tabIndex={tabIndex}
      data-e2e={dataE2e}
      onClick={onClick}
      disabled={disabled}
      className={className}
    />
  );

  if (!tooltipTitleId) return button;

  return (
    <Tooltip titleId={tooltipTitleId} placement="top">
      {button}
    </Tooltip>
  );
};

/**
 * Figma's Icon button at Size=Small. Every value is stated because antd's own small button differs on
 * all four, and tripled because `.ant-btn.ant-btn-sm.ant-btn-icon-only` weighs three classes.
 *
 * Scoped to this size so the other call sites keep antd's middle geometry.
 */
const smallIconButton = css`
  &&& {
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: var(--padding-xxs);
    border: none;
    border-radius: var(--border-radius-100);

    .anticon {
      font-size: 16px;
    }
  }
`;

const StyledIconButton = styled(Button)`
  color: var(--icon-primary-standalone);

  &:not(:disabled):hover {
    color: var(--icon-primary-hover);
  }

  /* The design's grey hover surface (Figma EZ Design system, node 114-2430). Doubled class because antd
     declares the hover background at two classes and its tag is injected later; up to antd 5.6 antd's own
     grey outranked this rule anyway, and 5.29 lets it through, so it has to be the design's colour. */
  &&:not(:disabled):hover {
    background-color: var(--surface-main-element-on-card);
  }

  &:disabled {
    color: var(--icon-disabled);
  }

  ${({ size }) => size === 'small' && smallIconButton}
`;
